// Marketing · Resumen — gasto vs presupuesto, costo por lead / por venta y
// ROAS por canal y por campaña, serie diaria, embudo y alertas.
// Fuente única: GET /api/v1/admin/marketing/resumen (org del JWT).

import { useCallback, useEffect, useState } from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';
import { AlertTriangle, BadgeCheck, CircleDollarSign, Info, RefreshCw, Target, TrendingUp, UserPlus, XOctagon } from 'lucide-react';

import { Button, Card, PageHeader } from '@/modules/orion-shell/ui';

import { demoActiva, fetchResumen, type Alerta, type Resumen } from '../api';
import {
  canalLabel, canalTone, ChartBox, DemoChip, dinero, ESTADO, EstadoCarga, fechaCorta, hoyCivil, Indicador, mesLargo, num, sumarDias,
} from '../shared';

type Periodo = 'mes' | '7' | '30';
const PERIODOS: { key: Periodo; label: string }[] = [
  { key: 'mes', label: 'Este mes' },
  { key: '7', label: '7 días' },
  { key: '30', label: '30 días' },
];

function rango(p: Periodo): [string, string] {
  const hoy = hoyCivil();
  if (p === 'mes') return [`${hoy.slice(0, 7)}-01`, hoy];
  return [sumarDias(hoy, -(Number(p) - 1)), hoy];
}

const RITMO: Record<string, { label: string; cls: string }> = {
  ok: { label: 'Gasto al ritmo del mes', cls: 'bg-primary/10 text-primary' },
  alto: { label: 'Gastando más rápido que el mes', cls: 'bg-destructive/10 text-destructive' },
  bajo: { label: 'Gastando más lento que el mes', cls: 'bg-muted text-muted-foreground' },
};

function BarraProgreso({ valor, tope }: { valor: number; tope: number }) {
  const pct = tope > 0 ? Math.min(100, Math.round((valor / tope) * 100)) : 0;
  return (
    <div
      className="h-2 w-full overflow-hidden rounded-full bg-muted"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
    >
      <div className={`h-full rounded-full ${pct >= 100 ? 'bg-destructive' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function IconoAlerta({ a }: { a: Alerta }) {
  if (a.severidad === 'critical') return <XOctagon size={16} className="text-destructive" aria-hidden />;
  if (a.severidad === 'warn') return <AlertTriangle size={16} className="text-destructive" aria-hidden />;
  return <Info size={16} className="text-primary" aria-hidden />;
}

export function ResumenView() {
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [data, setData] = useState<Resumen | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [demo, setDemoState] = useState(demoActiva());

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [desde, hasta] = rango(periodo);
      setData(await fetchResumen(desde, hasta));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
      setDemoState(demoActiva());
    }
  }, [periodo]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const m = data?.moneda || 'MXN';
  const t = data?.totales;

  return (
    <div className="mx-auto w-full max-w-6xl p-4 sm:p-6">
      <PageHeader
        title="Marketing"
        subtitle="Cuánto inviertes, qué te trae cada canal y cuánto cuesta cada cliente."
        actions={
          <>
            {demo && <DemoChip />}
            <div className="flex rounded-xl border border-border bg-card p-0.5" role="group" aria-label="Periodo">
              {PERIODOS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPeriodo(p.key)}
                  aria-pressed={periodo === p.key}
                  className={`rounded-[10px] px-3 py-1.5 text-sm transition-colors ${
                    periodo === p.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Button variant="ghost" onClick={() => void cargar()} aria-label="Actualizar">
              <RefreshCw size={16} aria-hidden />
            </Button>
          </>
        }
      />

      <EstadoCarga loading={loading && !data} error={error} onRetry={() => void cargar()} onDemo={() => void cargar()}>
        {data && t && (
          <div className="space-y-4">
            {/* Presupuesto del mes */}
            <Card
              title={data.presupuesto ? `Presupuesto de ${mesLargo(data.presupuesto.mes)}` : 'Presupuesto del mes'}
              action={
                data.presupuesto && (
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${RITMO[data.presupuesto.ritmo]?.cls ?? RITMO.ok.cls}`}>
                    {RITMO[data.presupuesto.ritmo]?.label ?? data.presupuesto.ritmo}
                  </span>
                )
              }
            >
              {data.presupuesto ? (
                <div className="space-y-4">
                  <div>
                    <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-2xl font-semibold tabular-nums text-foreground">
                        {dinero(data.presupuesto.gastado_cents, m)}
                      </span>
                      <span className="text-sm text-muted-foreground">de {dinero(data.presupuesto.tope_cents, m)}</span>
                    </div>
                    <BarraProgreso valor={data.presupuesto.gastado_cents} tope={data.presupuesto.tope_cents} />
                  </div>
                  {data.presupuesto.por_canal.length > 0 && (
                    <ul className="grid gap-3 sm:grid-cols-2">
                      {data.presupuesto.por_canal.map((c) => (
                        <li key={c.canal} className="min-w-0">
                          <div className="mb-1 flex justify-between gap-2 text-xs">
                            <span className="truncate font-medium text-foreground">{canalLabel(c.canal)}</span>
                            <span className="shrink-0 tabular-nums text-muted-foreground">
                              {dinero(c.gastado_cents ?? 0, m)} / {dinero(c.tope_cents, m)}
                            </span>
                          </div>
                          <BarraProgreso valor={c.gastado_cents ?? 0} tope={c.tope_cents} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Aún no hay presupuesto para este mes. Fíjalo en la pestaña «Presupuesto» para ver el ritmo de gasto.
                </p>
              )}
            </Card>

            {/* Indicadores */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
              <Indicador icon={<CircleDollarSign size={18} />} label="Inversión" value={dinero(t.gasto_cents, m)}
                sub={`${num(t.impresiones)} impresiones · ${num(t.clics)} clics`} tone="violet" />
              <Indicador icon={<UserPlus size={18} />} label="Leads" value={num(t.leads)} tone="blue" />
              <Indicador icon={<Target size={18} />} label="Costo por lead" value={dinero(t.cpl_cents, m)} tone="indigo" />
              <Indicador icon={<BadgeCheck size={18} />} label="Ventas" value={num(t.compras)}
                sub={t.cpa_cents != null ? `${dinero(t.cpa_cents, m)} por venta` : 'Sin ventas atribuidas'} tone="purple" />
              <Indicador icon={<TrendingUp size={18} />} label="Retorno (ROAS)"
                value={t.roas != null ? `${t.roas.toLocaleString('es-MX', { maximumFractionDigits: 2 })}×` : '—'}
                sub={t.ingreso_cents ? `${dinero(t.ingreso_cents, m)} vendidos` : undefined} tone="magenta" />
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              {/* Serie diaria */}
              <Card title="Inversión y leads por día" className="lg:col-span-2">
                {data.serie_diaria.some((d) => d.gasto_cents > 0 || d.leads > 0) ? (
                  <ChartBox>
                    {(width) => (
                      <ComposedChart width={width} height={224} data={data.serie_diaria} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                        <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                        <XAxis dataKey="fecha" tickLine={false} axisLine={false} minTickGap={16}
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} tickFormatter={fechaCorta} />
                        <YAxis yAxisId="g" tickLine={false} axisLine={false} width={48}
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                          tickFormatter={(v: number) => dinero(v, m)} />
                        <YAxis yAxisId="l" orientation="right" allowDecimals={false} tickLine={false} axisLine={false} width={28}
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} />
                        <Tooltip
                          cursor={{ fill: 'hsl(var(--muted))' }}
                          contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--popover-foreground))', fontSize: 12 }}
                          labelFormatter={(d: string) => fechaCorta(d)}
                          formatter={(v: number, k: string) => (k === 'gasto_cents' ? [dinero(v, m), 'Inversión'] : [num(v), k === 'leads' ? 'Leads' : 'Ventas'])}
                        />
                        <Bar yAxisId="g" dataKey="gasto_cents" fill="hsl(var(--primary))" fillOpacity={0.85} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
                        <Line yAxisId="l" dataKey="leads" type="linear" stroke="var(--tone-blue, hsl(var(--foreground)))" strokeWidth={2} dot={false} isAnimationActive={false} />
                      </ComposedChart>
                    )}
                  </ChartBox>
                ) : (
                  <p className="py-10 text-center text-sm text-muted-foreground">Sin inversión ni leads en este periodo.</p>
                )}
              </Card>

              {/* Embudo */}
              <Card title="Embudo">
                <ol className="space-y-3">
                  {[
                    ['Visitas', data.embudo.visitas],
                    ['Leads', data.embudo.leads],
                    ['Empezaron a pagar', data.embudo.checkouts],
                    ['Compraron', data.embudo.compras],
                  ].map(([label, v], i, arr) => {
                    const top = (arr[0][1] as number) || 0;
                    const pct = top > 0 ? Math.max(2, Math.round(((v as number) / top) * 100)) : 0;
                    return (
                      <li key={label as string}>
                        <div className="mb-1 flex justify-between text-sm">
                          <span className="text-foreground">{label}</span>
                          <span className="font-semibold tabular-nums text-foreground">{num(v as number)}</span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full bg-muted">
                          <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%`, opacity: 1 - i * 0.15 }} />
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </Card>
            </div>

            {/* Alertas */}
            {data.alertas.length > 0 && (
              <Card title="Alertas">
                <ul className="space-y-2">
                  {data.alertas.map((a, i) => (
                    <li key={i} className="flex items-start gap-2 rounded-xl border border-border bg-muted/30 px-3 py-2 text-sm text-foreground">
                      <span className="mt-0.5 shrink-0"><IconoAlerta a={a} /></span>
                      <span>{a.mensaje}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Por canal */}
            <Card title="Por canal">
              {data.por_canal.length ? (
                <ul className="divide-y divide-border">
                  <Encabezado cols={['Canal', 'Inversión', 'Leads', 'Costo por lead', 'Ventas']} />
                  {data.por_canal.map((c) => (
                    <li key={c.canal} className="grid grid-cols-2 gap-x-4 gap-y-1 py-3 text-sm sm:grid-cols-[minmax(0,2fr)_repeat(4,minmax(0,1fr))] sm:items-center">
                      <span className="col-span-2 flex min-w-0 items-center gap-2 font-medium text-foreground sm:col-span-1">
                        <span className={`orion-tone tone-${canalTone(c.canal)} h-2.5 w-2.5 shrink-0 rounded-full`} style={{ background: 'var(--tone, hsl(var(--primary)))' }} aria-hidden />
                        <span className="truncate">{canalLabel(c.canal)}</span>
                      </span>
                      <Dato label="Inversión" v={dinero(c.gasto_cents, m)} />
                      <Dato label="Leads" v={num(c.leads)} />
                      <Dato label="Costo por lead" v={dinero(c.cpl_cents, m)} />
                      <Dato label="Ventas" v={`${num(c.compras)}${c.cpa_cents != null ? ` · ${dinero(c.cpa_cents, m)} c/u` : ''}`} />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">Todavía no hay resultados por canal en este periodo.</p>
              )}
            </Card>

            {/* Por campaña */}
            <Card title="Por campaña">
              {data.por_campana.length ? (
                <ul className="divide-y divide-border">
                  <Encabezado cols={['Campaña', 'Inversión', 'Clics', 'Leads', 'Costo por lead']} />
                  {data.por_campana.map((c) => (
                    <li key={c.registry_id} className="grid grid-cols-2 gap-x-4 gap-y-1 py-3 text-sm sm:grid-cols-[minmax(0,2fr)_repeat(4,minmax(0,1fr))] sm:items-center">
                      <span className="col-span-2 min-w-0 sm:col-span-1">
                        <span className="block truncate font-medium text-foreground" title={c.nombre}>{c.nombre}</span>
                        <span className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                          <span className="rounded-full bg-muted px-2 py-0.5">{ESTADO[c.estado] ?? c.estado}</span>
                          <span className="truncate">{canalLabel(c.canal)} · {c.cuenta}</span>
                        </span>
                      </span>
                      <Dato label="Inversión" v={dinero(c.gasto_cents, m)} />
                      <Dato label="Clics" v={num(c.clics)} />
                      <Dato label="Leads" v={num(c.leads)} />
                      <Dato label="Costo por lead" v={dinero(c.cpl_cents, m)} />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">Sin campañas registradas en este periodo.</p>
              )}
            </Card>

            <p className="text-xs text-muted-foreground">
              Actualizado: {new Date(data.actualizado).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })} ·
              El gasto de cada campaña se cuenta en la organización a la que pertenece, aunque la pague otra cuenta.
            </p>
          </div>
        )}
      </EstadoCarga>
    </div>
  );
}

function Dato({ label, v }: { label: string; v: string }) {
  return (
    <span className="min-w-0">
      <span className="block text-xs text-muted-foreground sm:hidden">{label}</span>
      <span className="block truncate tabular-nums text-foreground" title={label}>{v}</span>
    </span>
  );
}

function Encabezado({ cols }: { cols: string[] }) {
  return (
    <li className="hidden pb-2 text-xs font-medium text-muted-foreground sm:grid sm:grid-cols-[minmax(0,2fr)_repeat(4,minmax(0,1fr))] sm:gap-x-4" aria-hidden>
      {cols.map((c) => (
        <span key={c}>{c}</span>
      ))}
    </li>
  );
}
